import { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import { Loader2, Users, CheckCircle2, XCircle, Zap } from "lucide-react";

export default function InviteAccept() {
  const params = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const token = params.token;

  const [inviteInfo, setInviteInfo] = useState<{ workspaceName: string; invitedByName: string; role: string; status: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    fetch("/api/auth/user", { credentials: "include" })
      .then(r => { setIsAuthenticated(r.ok); })
      .catch(() => setIsAuthenticated(false));
  }, []);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/team-invite/${token}`)
      .then(r => {
        if (!r.ok) throw new Error("Invite not found or expired");
        return r.json();
      })
      .then(data => { setInviteInfo(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  const acceptInvite = async () => {
    setAccepting(true);
    try {
      const res = await fetch(`/api/team-invite/${token}/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to accept invitation");
      setAccepted(true);
      setTimeout(() => setLocation("/"), 2000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAccepting(false);
    }
  };

  const roleLabel = inviteInfo?.role === "marketing_team" ? "Marketing Team Partner" : "Team Member";

  return (
    <div className="min-h-screen bg-[#030308] flex items-center justify-center px-6" data-testid="page-invite-accept">
      <div className="max-w-md w-full text-center">
        <div className="flex items-center justify-center gap-2 mb-10">
          <Zap size={20} className="text-blue-400" />
          <span className="text-xl font-display font-black tracking-tight text-white">Drpr<span className="text-blue-400">.ai</span></span>
        </div>

        {loading && (
          <div>
            <Loader2 size={32} className="text-blue-400 animate-spin mx-auto mb-4" />
            <p className="text-white/40 text-sm">Loading invitation...</p>
          </div>
        )}

        {!loading && error && (
          <div data-testid="state-invite-error">
            <div className="w-16 h-16 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center mx-auto mb-6">
              <XCircle size={28} className="text-destructive" />
            </div>
            <h2 className="text-2xl font-display font-bold text-white mb-3">Invite Not Found</h2>
            <p className="text-white/40 text-sm mb-8">{error}</p>
            <a href="/" className="inline-flex items-center justify-center px-6 py-3 bg-white/5 border border-white/10 text-white/60 hover:text-white rounded-xl text-sm font-medium transition-all">
              Go to Drpr.ai
            </a>
          </div>
        )}

        {!loading && !error && accepted && (
          <div data-testid="state-invite-accepted">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={28} className="text-emerald-400" />
            </div>
            <h2 className="text-2xl font-display font-bold text-white mb-3">You're in!</h2>
            <p className="text-white/40 text-sm">Redirecting you to {inviteInfo?.workspaceName}...</p>
          </div>
        )}

        {!loading && !error && !accepted && inviteInfo && (
          <div data-testid="state-invite-info">
            <div className="w-16 h-16 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-6">
              <Users size={28} className="text-blue-400" />
            </div>
            <h2 className="text-2xl font-display font-bold text-white mb-3">
              You've been invited
            </h2>
            <p className="text-white/50 text-sm mb-2">
              <span className="text-white font-semibold">{inviteInfo.invitedByName}</span> has invited you to join
            </p>
            <p className="text-xl font-display font-bold text-white mb-2">{inviteInfo.workspaceName}</p>
            <p className="text-white/30 text-xs mb-8">as a <span className="text-blue-400">{roleLabel}</span></p>

            {isAuthenticated === null && (
              <Loader2 size={20} className="text-blue-400 animate-spin mx-auto" />
            )}

            {isAuthenticated === true && (
              <button
                onClick={acceptInvite}
                disabled={accepting}
                className="inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-bold rounded-xl text-sm transition-all shadow-xl shadow-blue-900/30 disabled:opacity-60"
                data-testid="button-accept-invite"
              >
                {accepting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {accepting ? "Accepting..." : "Accept Invitation"}
              </button>
            )}

            {isAuthenticated === false && (
              <div className="space-y-3">
                <a
                  href={`/join?redirect=${encodeURIComponent(`/invite/${token}`)}`}
                  className="inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-bold rounded-xl text-sm transition-all shadow-xl shadow-blue-900/30"
                  data-testid="button-login-to-accept"
                >
                  Log in to Accept
                </a>
                <p className="text-white/25 text-xs">You'll be taken back here after signing in</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
